<script setup lang="ts">
import type { Item } from '@/components/menuLists/SavedFiltersMenuList.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { onMounted, onUnmounted, ref } from 'vue';
import { isCancel } from 'axios';

import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import SavedFiltersMenuList from '@/components/menuLists/SavedFiltersMenuList.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';
import { fetchGetFilters } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  deleteCompanyFilter?: (itemId: number) => Promise<unknown>;
  deleteMyFilter?: (itemId: number) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  deleteCompanyFilter: undefined,
  deleteMyFilter: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
}>();

function handleCancelClick(event: Event): void {
  emit('cancelBtnClick', event);
}

let controller: AbortController | undefined;
const isFetchingData = ref(false);
const fetchedData = ref<openapi.GetFilters_200>();
const companyFilters = ref<Item[]>([]);
const myFilters = ref<Item[]>([]);

function clear(): void {
  if (controller) controller.abort();
}

onMounted(async () => {
  await fetchData();
  updateResults();
});

onUnmounted(() => {
  clear();
});

async function fetchData(): Promise<void> {
  fetchedData.value = undefined;
  if (controller) controller.abort();
  isFetchingData.value = true;
  controller = new AbortController();
  await fetchGetFilters(
    { route: routeNames.SEARCH_ADVANCED },
    { signal: controller.signal },
  )
    .then((data) => {
      fetchedData.value = data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) throw err;
    })
    .finally(() => {
      isFetchingData.value = false;
    });
}

function updateResults(): void {
  if (!fetchedData.value) return;
  companyFilters.value = [];
  myFilters.value = [];
  fetchedData.value.forEach((item) => {
    if (item.type === 'personal') {
      myFilters.value.push(item);
    }
    if (item.type === 'company') {
      companyFilters.value.push(item);
    }
  });
}

async function deleteCompanyFilterWrapper(filterId: number): Promise<void> {
  if (!props.deleteCompanyFilter) return;
  await props.deleteCompanyFilter(filterId);
  await fetchData();
  updateResults();
}

async function deleteMyFilterWrapper(filterId: number): Promise<void> {
  if (!props.deleteMyFilter) return;
  await props.deleteMyFilter(filterId);
  await fetchData();
  updateResults();
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.savedFiltersDialog"
    data-testid="savedFiltersDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Saved Filters
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <h4 :class="$style.h4">Company</h4>
      <SavedFiltersMenuList
        :delete="deleteCompanyFilterWrapper"
        :items="companyFilters"
        :is-loading="isFetchingData"
      />
      <h4 :class="$style.h4">My</h4>
      <SavedFiltersMenuList
        :delete="deleteMyFilterWrapper"
        :items="myFilters"
        :is-loading="isFetchingData"
      />
    </section>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.savedFiltersDialog {
  line-height: 1.4;
  width: variables.$reportGenerateDialogWidth;
}
  .main {
    margin-bottom: -4px;
  }
    .h4 {
      margin: variables.$pageDialogWindowMainWrapperMargin 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
      font-size: 1rem;
      font-weight: 500;
    }
</style>
